import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  distinctUntilChanged,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { AvailableDate, Currency, Hostel } from '@app/services/api/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { ScrollService } from '@app/scroll.service';
import { DateHelper } from '@app/date-helper';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SearchSuggestion } from '@app/explore/places.service';
import { HttpParams } from '@angular/common/http';
import { EnvironmentService } from '@app/environment.service';
import { LocaleService } from '@app/locale.service';
import { SearchService } from '@app/search.service';

@Component({
  selector: 'app-room-filters',
  templateUrl: './room-filters.component.html',
  styleUrls: ['./room-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomFiltersComponent {
  @Input()
  public unavailableDates: AvailableDate[];
  @Input()
  public noAvailability = false;

  public searchForm: FormGroup;
  public whereValue: any;

  // public bookingOptions: Observable<HostelSearchParams> = this.searchService.searchParams$;
  public bookingOptions: any;

  @Input()
  public hostel: Hostel;

  @Input() public isInHeader: boolean = false;

  private destroyed = new Subject<void>();
  public focused = new BehaviorSubject(false);

  public activeInput: string = ''; // Added this property for active input tracking

  selection: any;
  days = Array.from({ length: 40 }, (_, i) => i + 1);
  public selecting: boolean = false;

  anchorStart?: number;
  anchorEnd?: number;
  focus?: number;

  fromMonth!: number;
  fromYear!: number;

  toMonth!: number;
  toYear!: number;

  toDays!: number;
  fromDays!: number;

  toStartDay!: number;
  fromStartDay!: number;

  private touched = false;

  public edges = [];
  public corners = [];

  public startDayOfMonth: number | undefined;
  public endDayOfMonth: number | undefined;

  public absoluteAnchorStart: number | undefined = 0;
  public absoluteAnchorEnd: number | undefined = 0;
  public monthOffset = 0;

  public startDate: Date | undefined;
  public endDate: Date | undefined;

  /**
   * Must be an ISOString
   */
  public anchorStartDate: string;

  /**
   * Must be an ISOString
   */
  public anchorEndDate: string;

  public noOfGuestsFocused = new BehaviorSubject(false);

  public initialAnchorStart: any;
  public initialAnchorEnd: any;
  public initialAnchorStartDate: any;
  public initialAnchorEndDate: any;

  public currency: ReplaySubject<Currency>;

  constructor(
    private readonly route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: string,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private scrollService: ScrollService,
    private environment: EnvironmentService,
    private localeService: LocaleService
  ) {}

  public isHostel(val: SearchSuggestion): boolean {
    if (!val) {
      return false;
    }

    return val.searchType === 2;
  }

  ngOnInit() {
    this.currency = this.localeService.currentCurrency;

    this.searchForm = new FormGroup({
      where: new FormControl('', [Validators.required]),
    });

    this.noOfGuestsFocused
      .pipe(
        distinctUntilChanged(),
        tap((focused) => {
          // when done updating # of guests, navigate
          if (!focused && this.bookingOptions) {
            this.searchService.setSearchParam('noOfGuests', this.bookingOptions?.noOfGuests);
            const currentUrl = this.router.url.split('?')[0];
            this.scrollService.saveScrollPosition(currentUrl);
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {
                noOfGuests: this.bookingOptions?.noOfGuests ?? 1,
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          }
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();

    // todo normalize these
    this.fromMonth = new Date().getMonth();
    this.fromYear = new Date().getFullYear();
    this.toYear = this.fromMonth === 11 ? this.fromYear + 1 : this.fromYear;
    this.toMonth = this.fromMonth === 11 ? 0 : this.fromMonth + 1;

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);

    if (isPlatformBrowser(this.platformId)) {
      this.route.queryParamMap
        .pipe(takeUntil(this.destroyed))
        .subscribe((paramsAsMap: any) => {
          if (paramsAsMap.has('checkInDate')) {
            this.anchorStartDate = paramsAsMap.get('checkInDate');
          }

          if (paramsAsMap.has('checkOutDate')) {
            this.anchorEndDate = paramsAsMap.get('checkOutDate');
          }

          this.bookingOptions = {
            // ...paramsAsMap.params,
            currency: paramsAsMap.get('currency') ?? 'USD',
            checkInDate: paramsAsMap.get('checkInDate'),
            checkOutDate: paramsAsMap.get('checkOutDate'),
            noOfGuests: Number(paramsAsMap.get('noOfGuests') ?? 1),
          };
          
          // Sync with SearchService
          if (paramsAsMap.has('checkInDate')) {
            this.searchService.setSearchParam('checkInDate', paramsAsMap.get('checkInDate'));
          }
          if (paramsAsMap.has('checkOutDate')) {
            this.searchService.setSearchParam('checkOutDate', paramsAsMap.get('checkOutDate'));
          }
          if (paramsAsMap.has('noOfGuests')) {
            this.searchService.setSearchParam('noOfGuests', Number(paramsAsMap.get('noOfGuests') ?? 1));
          }
          
          this.cdr.detectChanges();
        });
    }
  }

  // ngOnChanges(changes: SimpleChanges) {
  //   if (changes['hostel']?.currentValue) {
  //     console.log(this.hostel);
  //     this.searchForm.patchValue({'where': this.hostel});
  //     this.searchForm.updateValueAndValidity();
  //     console.log(this.searchForm.value);
  //   }
  // }

  public setActiveInput(inputName: string) {
    this.activeInput = inputName;
  }

  // is this working correctly?
  public highlight(i: number) {
    if (this.anchorStart !== undefined) {
      if (i > this.anchorStart) {
        this.focus = i;
      }
    }
  }

  public unhighlight() {
    this.focus = undefined;
  }

  setFocus(focused: boolean) {
    if (!focused && this.startDate && this.endDate) {
      this.doUpdate();
    }
    this.focused.next(focused);
  }

  public close() {
    this.focused.next(false);
    this.cdr.detectChanges();
  }

  public doUpdate() {
    this.selecting = false;
    this.anchorStartDate = DateHelper.normalizeDateToDateString(this.startDate);
    this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
    this.bookingOptions.checkInDate = DateHelper.normalizeDateToDateString(
      this.startDate
    );
    this.bookingOptions.checkOutDate = DateHelper.normalizeDateToDateString(
      this.endDate
    );

    // Update the search service with new dates
    this.searchService.setSearchParam('checkInDate', this.anchorStartDate);
    this.searchService.setSearchParam('checkOutDate', this.anchorEndDate);

    const currentUrl = this.router.url.split('?')[0];
    this.anchorStart = undefined;
    this.anchorEnd = undefined;
    this.scrollService.saveScrollPosition(currentUrl);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        checkInDate: this.anchorStartDate,
        checkOutDate: this.anchorEndDate,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public clicked(day: number, dayOfMonth: number, checkDisabled = false) {
    const today = new Date().getDate();
    if (
      checkDisabled &&
      this.monthOffset === 0 &&
      today + this.fromStartDay > day
    ) {
      return;
    }
    this.selecting = true;
    if (
      this.absoluteAnchorStart &&
      this.absoluteAnchorStart < this.monthOffset
    ) {
      // click on a future month
      this.absoluteAnchorEnd = this.monthOffset;
      this.anchorEnd = day;
      this.endDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
      this.endDayOfMonth = dayOfMonth;
      this.done(day);
    } else if (
      this.absoluteAnchorStart &&
      this.absoluteAnchorStart > this.monthOffset
    ) {
      // click on a previous month
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.absoluteAnchorEnd = undefined;
      this.anchorEnd = undefined;
      this.endDate = undefined;
      this.anchorStartDate = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
    } else if (day === this.anchorStart) {
      this.anchorStart = undefined;
      this.anchorStartDate = undefined;
      this.startDate = undefined;
      this.absoluteAnchorStart = undefined;
      this.absoluteAnchorEnd = undefined;
      this.startDayOfMonth = undefined;
      this.endDayOfMonth = undefined;
    } else if (this.anchorStart !== undefined && this.anchorEnd !== undefined) {
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.absoluteAnchorEnd = undefined;
      this.anchorEnd = undefined;
      this.anchorEndDate = undefined;
      this.endDate = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
    } else if (this.anchorStart !== undefined && day > this.anchorStart) {
      this.absoluteAnchorEnd = this.monthOffset;
      this.anchorEnd = day;
      this.endDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
      this.endDayOfMonth = dayOfMonth;
      this.done(day);
    } else {
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.anchorEnd = undefined;
      this.anchorEndDate = undefined;
      this.endDate = undefined;
      this.absoluteAnchorEnd = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
    }
  }

  private getDate(selected: number): Date {
    let year;
    let month;
    let day;

    if (selected > 40) {
      year = this.toYear;
      month = this.toMonth;
      day = selected - 40 - this.toStartDay;
    } else {
      year = this.fromYear;
      month = this.fromMonth;
      day = selected - this.fromStartDay;
    }

    return new Date(year, month, day);
  }

  public done(day: number) {
    // this.doUpdate();
    this.selecting = false;
  }

  reset() {
    this.anchorStart = this.initialAnchorStart;
    this.anchorStartDate = this.initialAnchorStartDate;
    this.anchorEnd = this.initialAnchorEnd;
    this.anchorEndDate = this.initialAnchorEndDate;
    this.absoluteAnchorEnd = undefined;
    this.absoluteAnchorStart = undefined;
    // this.value = undefined;
    this.startDayOfMonth = undefined;
    this.endDayOfMonth = undefined;
    this.selecting = false;
    // this.onChange(this.value);
  }

  public nextMonth() {
    if (this.anchorStart !== undefined) {
      this.anchorStart = this.anchorStart - 40;
    }
    if (this.anchorEnd !== undefined) {
      this.anchorEnd = this.anchorEnd - 40;
    }
    this.monthOffset = this.monthOffset + 1;
    this.fromMonth = this.toMonth;
    this.fromYear = this.toYear;
    this.toYear = this.toMonth === 11 ? this.toYear + 1 : this.toYear;
    this.toMonth = this.fromMonth === 11 ? 0 : this.toMonth + 1;

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);
  }

  // shift anchors by 40! should I do this? seems like it messes things up
  public previousMonth() {
    if (this.anchorStart !== undefined) {
      this.anchorStart = this.anchorStart + 40;
    }
    if (this.anchorEnd !== undefined) {
      this.anchorEnd = this.anchorEnd + 40;
    }
    this.monthOffset = this.monthOffset - 1;
    this.toMonth = this.fromMonth;
    this.toYear = this.fromYear;
    this.fromYear = this.fromMonth === 0 ? this.fromYear - 1 : this.fromYear;
    if (this.fromMonth === 0) {
      this.fromMonth = 11;
    } else {
      this.fromMonth = this.fromMonth - 1;
    }

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);
  }

  openCalendar() {
    this.focused.next(true);
    this.initialAnchorStart = this.anchorStart;
    this.initialAnchorEnd = this.anchorEnd;
    this.initialAnchorStartDate = this.anchorStartDate;
    this.initialAnchorEndDate = this.anchorEndDate;
  }

  public increment() {
    this.bookingOptions.noOfGuests = +this.bookingOptions.noOfGuests + 1;
  }

  public decrement() {
    this.bookingOptions.noOfGuests = +this.bookingOptions.noOfGuests - 1;
  }

  public goToSearchResults() {
    const where = this.whereValue || this.searchForm.get('where')?.value;
    const when = this.bookingOptions?.checkInDate
      ? [this.bookingOptions.checkInDate, this.bookingOptions.checkOutDate]
      : undefined;
    const who = this.bookingOptions?.noOfGuests ?? 1;

    const params = {};

    // Navigate to the search results page or handle the search logic here
    if (!where || !this.isHostel(where)) {
      if (when) {
        params['start'] = `${when[0]}T00:00:00.000Z`;
        params['end'] = `${when[1]}T00:00:00.000Z`;
      }
      if (who) {
        params['number_of_guests'] = who;
      }
      if (where) {
        if (where.cityId) {
          params['city_id'] = where.cityId;
        }
        if (where.stateId) {
          params['state_id'] = where.stateId;
        }
        if (where.countryId) {
          params['country_id'] = where.countryId;
        }
        params['search_key'] = this.getPlaceSearchKey(where);
        params['search_display_text'] = this.getPlaceSearchDisplayText(where);
        params['search_type'] = where.searchType;
      } else {
        params['search_key'] = 'anywhere';
        params['search_type'] = 7;
        params['search_display_text'] = 'Anywhere';
      }

      const queryString = new HttpParams({ fromObject: params }).toString();
      window.location.href = `${this.environment.pwaUrl}?${queryString}`;
    } else {
      if (when) {
        params['checkInDate'] = when[0];
        params['checkOutDate'] = when[1];
      }
      if (who) {
        params['noOfGuests'] = who;
      }

      this.router.navigate(['/hostels', where.id], { queryParams: params });
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  getPlaceSearchDisplayText(where: any) {
    return where.city
      ? `${where.city}, ${where.country}`
      : where.state
      ? `${where.state}, ${where.country}`
      : where.country;
  }

  getPlaceSearchKey(where: any) {
    return where.city ?? where.cityName ?? where.state ?? where.country;
  }
  
  onWhereChange(value: any) {
    this.whereValue = value;
    console.log('Where value changed:', value);
  }
}
